import type {
  ProjectCapacitySummary,
  ProjectCardView,
  ProjectDashboardSummary,
  ProjectRecord,
  ProjectStatus,
  SubscriptionTier,
  UpgradePromptState,
} from "@/types/projects";

export const PROJECT_LIMITS: Record<SubscriptionTier, number> = {
  free: 3,
  crafter: 10,
  author: 20,
};

export const DASHBOARD_SECTION_IDS = [
  "dashboard",
  "lore",
  "stories",
  "entities",
  "affinity-chart",
  "timeline",
] as const;

export function sortProjectsByLastModified(projects: ProjectCardView[]) {
  return [...projects].sort(
    (left, right) =>
      new Date(right.lastModifiedAt).getTime() - new Date(left.lastModifiedAt).getTime(),
  );
}

export function upsertProjectCard(projects: ProjectCardView[], project: ProjectCardView) {
  const nextProjects = projects.filter(
    (candidate) => candidate.projectId !== project.projectId,
  );
  nextProjects.push(project);
  return sortProjectsByLastModified(nextProjects);
}

export function removeProjectCard(projects: ProjectCardView[], projectId: string) {
  return projects.filter((project) => project.projectId !== projectId);
}

export function selectProjectsByStatus(
  projects: ProjectCardView[],
  status: ProjectStatus,
) {
  return sortProjectsByLastModified(
    projects.filter((project) => project.status === status),
  );
}

export function mapProjectRecordToCard(
  project: ProjectRecord,
  coverImageUrl: string | null,
): ProjectCardView {
  return {
    projectId: project.id,
    title: project.title,
    genre: project.genre,
    status: project.status as ProjectStatus,
    coverImagePath: project.cover_image_path,
    coverImageUrl,
    lastModifiedAt: project.updated_at,
  };
}

export function hasProjectArtDirection(
  project: Pick<ProjectRecord, "artstyle_ref_paths">,
) {
  return Array.isArray(project.artstyle_ref_paths)
    && project.artstyle_ref_paths.some(
      (path) => typeof path === "string" && path.trim().length > 0,
    );
}

export function mapCapacityRow(input: {
  projects_limit: number;
  projects_remaining: number;
  projects_used: number;
  tier: SubscriptionTier;
}): ProjectCapacitySummary {
  return {
    tier: input.tier,
    projectsLimit: input.projects_limit,
    projectsRemaining: Math.max(input.projects_remaining, 0),
    projectsUsed: input.projects_used,
  };
}

export function buildUpgradePrompt(
  tier: SubscriptionTier,
  currentCount: number,
  allowedCount: number,
  recommendedTier: SubscriptionTier,
): UpgradePromptState {
  return {
    reason: "project_limit_reached",
    tier,
    currentCount,
    allowedCount,
    recommendedTier,
  };
}

export function buildProjectDashboardSummary(
  project: ProjectCardView,
  capacity: ProjectCapacitySummary,
  counts?: {
    activeImportJob?: ProjectDashboardSummary["activeImportJob"];
    entityCardCount?: number;
    imageCount?: number;
    importReview?: ProjectDashboardSummary["importReview"];
    importReviewLock?: ProjectDashboardSummary["importReviewLock"];
    loreEntryCount?: number;
    recentActivity?: ProjectDashboardSummary["recentActivity"];
    timelineEventCount?: number;
    wordCount?: number;
  },
): ProjectDashboardSummary {
  return {
    ...project,
    wordCount: counts?.wordCount ?? 0,
    imageCount: counts?.imageCount ?? 0,
    loreEntryCount: counts?.loreEntryCount ?? 0,
    entityCardCount: counts?.entityCardCount ?? 0,
    timelineEventCount: counts?.timelineEventCount ?? 0,
    recentActivity: counts?.recentActivity ?? [],
    capacity,
    activeImportJob: counts?.activeImportJob ?? null,
    importReview: counts?.importReview ?? null,
    importReviewLock: counts?.importReviewLock ?? null,
  };
}

export function getCapacityState(
  capacity: ProjectCapacitySummary | null,
  upgradePrompt: UpgradePromptState | null,
) {
  if (upgradePrompt) {
    return "upgrade";
  }

  if (!capacity) {
    return "idle";
  }

  if (capacity.projectsRemaining === 0) {
    return "warning";
  }

  return "available";
}
